import React, { useState, useContext, useRef } from "react";
import { Modal } from "reactstrap";
import UserContext from "../../context/UserContext";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import { AuthService, MinorConsentData } from "../../services/AuthService";

interface MinorConsentModalNCProps {
  isOpen: boolean;
  toggle: () => void;
}



const MinorConsentModalNC: React.FC<MinorConsentModalNCProps> = ({ isOpen, toggle }) => {
  const [user] = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form fields
  const [clientName, setClientName] = useState("");
  const [clientDateOfBirth, setClientDateOfBirth] = useState("");
  const [clientPrimaryInsurance, setClientPrimaryInsurance] = useState("");
  const [clientSecondaryInsurance, setClientSecondaryInsurance] = useState("");
  const [parentLegalGuardianName, setParentLegalGuardianName] = useState("");
  const [parentLegalGuardianEmail, setParentLegalGuardianEmail] = useState("");
  const [parentLegalGuardianPhone, setParentLegalGuardianPhone] = useState("");
  
  // Signature refs
  const guardianSignatureRef = useRef<SignatureCanvas>(null);
  const clinicalDirectorSignatureRef = useRef<SignatureCanvas>(null);

  const clearGuardianSignature = () => {
    guardianSignatureRef.current?.clear();
  };

  const clearClinicalDirectorSignature = () => {
    clinicalDirectorSignatureRef.current?.clear();
  };

  const scrollToSignature = () => {
    const signatureSection = document.getElementById("signature-section");
    if (signatureSection) {
      signatureSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!clientName.trim()) {
      toast.error("Client Name is required");
      return;
    }
    if (!clientDateOfBirth.trim()) {
      toast.error("Client Date of Birth is required");
      return;
    }
    if (!parentLegalGuardianName.trim()) {
      toast.error("Parent/Legal Guardian Name is required");
      return;
    }
    if (!parentLegalGuardianEmail.trim()) {
      toast.error("Parent/Legal Guardian Email is required");
      return;
    }
    if (!parentLegalGuardianPhone.trim()) {
      toast.error("Parent/Legal Guardian Phone is required");
      return;
    }

    // Get signatures (optional)
    const guardianSignature = guardianSignatureRef.current?.isEmpty() ? undefined : guardianSignatureRef.current?.toDataURL();
    const clinicalDirectorSignature = clinicalDirectorSignatureRef.current?.isEmpty() ? undefined : clinicalDirectorSignatureRef.current?.toDataURL();

    try {
      setIsSubmitting(true);

      const currentDate = new Date().toISOString();

      const minorConsentData: MinorConsentData = {
        state: "North Carolina",
        clientName: clientName.trim(),
        clientDateOfBirth: clientDateOfBirth.trim(),
        clientPrimaryInsuranceNumber: clientPrimaryInsurance.trim() || undefined,
        clientSecondaryInsuranceNumber: clientSecondaryInsurance.trim() || undefined,
        parentLegalGuardianName: parentLegalGuardianName.trim(),
        parentLegalGuardianEmail: parentLegalGuardianEmail.trim(),
        parentLegalGuardianPhone: parentLegalGuardianPhone.trim(),
        guardianSignatureBase64: guardianSignature,
        guardianSignatureDate: guardianSignature ? currentDate : undefined,
        lavniClinicalDirectorSignatureBase64: clinicalDirectorSignature,
        lavniClinicalDirectorSignatureDate: clinicalDirectorSignature ? currentDate : undefined
      };

      console.log("Submitting minor consent data:", minorConsentData);

      // Call API through service (you'll need to add this method to AuthService)
      const result = await AuthService.createMinorConsentData(minorConsentData);
      console.log("Minor consent submission result:", result);

      if (!result.success) {
        throw new Error(result.message || "Failed to submit minor consent form");
      }

      toast.success("Minor consent form submitted successfully!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });

      toggle();
    } catch (error: any) {
      console.error("Error submitting minor consent:", error);
      toast.error(error.message || "Failed to submit minor consent form", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="modal-lg">
      <div className="modal-header">
        <h5 className="modal-title">Minor Consent Form Required</h5>
        <button
          type="button"
          onClick={toggle}
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span>&times;</span>
        </button>
      </div>
      <div style={{ position: "relative" }}>
        {/* Sticky Navigation Panel */}
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1000
          }}
        >
          {/* <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={scrollToSignature}
            style={{
              borderRadius: "20px",
              fontSize: "12px",
              padding: "6px 12px",
              fontWeight: "500",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              backgroundColor: "white"
            }}
          >
            ✍️ Sign to Complete
          </button> */}
        </div>

        <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "60px" }}>
          <div style={{ textAlign: "center", marginBottom: "30px" }}>
            <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>NORTH CAROLINA</h4>
            <h5 style={{ fontWeight: "bold" }}>PARENT/LEGAL GUARDIAN CONSENT FOR COUNSELING WITH MINORS</h5>
          </div>

          {/* Form Fields */}
          <div className="mb-3">
            <label className="form-label">Client Name:</label>
            <input
              type="text"
              className="form-control"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Client Date of Birth:</label>
            <input
              type="date"
              className="form-control"
              value={clientDateOfBirth}
              onChange={(e) => setClientDateOfBirth(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Client Primary Insurance Number:</label>
            <input
              type="text"
              className="form-control"
              value={clientPrimaryInsurance}
              onChange={(e) => setClientPrimaryInsurance(e.target.value)}
              placeholder="Enter primary insurance number"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Client Secondary Insurance Number:</label>
            <input
              type="text"
              className="form-control"
              value={clientSecondaryInsurance}
              onChange={(e) => setClientSecondaryInsurance(e.target.value)}
              placeholder="Enter secondary insurance number (optional)"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Parent/Legal Guardian Name:</label>
            <input
              type="text"
              className="form-control"
              value={parentLegalGuardianName}
              onChange={(e) => setParentLegalGuardianName(e.target.value)}
              placeholder="Enter parent/legal guardian name"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Parent/Legal Guardian Email Address:</label>
            <input
              type="email"
              className="form-control"
              value={parentLegalGuardianEmail}
              onChange={(e) => setParentLegalGuardianEmail(e.target.value)}
              placeholder="Enter email address"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Parent/Legal Guardian Phone Number:</label>
            <input
              type="tel"
              className="form-control"
              value={parentLegalGuardianPhone}
              onChange={(e) => setParentLegalGuardianPhone(e.target.value)}
              placeholder="Enter phone number"
            />
          </div>

          {/* Consent Text */}
          <div className="mb-4" style={{ lineHeight: "1.6" }}>
            <p>
              Lavni Incorporated provides counseling services to minors in need of social/emotional/behavioral support. As the 
              parent/legal guardian with authority to consent on behalf of the minor named above, I hereby give my consent for 
              the minor to receive counseling on a voluntary basis. I understand that I can withdraw my consent at any time. I 
              understand that this consent form remains valid until counseling is deemed no longer necessary.
            </p>
            
            <p>
              North Carolina law permits minors aged 12 and older to consent to outpatient mental health treatment, including 
              emotional or substance use services, without parental consent in certain circumstances. However, parental 
              involvement is encouraged whenever clinically appropriate. Access to a minor's mental health records may be 
              limited by law or professional judgment if it is determined that disclosure would pose a substantial risk of harm to 
              the minor.
            </p>
            
            <p>
              Lavni clinicians are professionally and legally mandated to report suspected abuse or if an individual is determined 
              to be at risk of serious bodily harm to self or others. If there are any questions, please contact Lavni's Clinical 
              Director, Crystal Bush, LCSW LCAS LISW-CP, at crystal@mylavni.com, Subject: Parent/Legal Guardian.
            </p>
          </div>

          {/* Signature Section */}
          <div id="signature-section" className="mb-4">
            <h6 className="mb-3">Signatures</h6>
            
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Guardian Signature:</label>
                <div style={{ border: "1px solid #ccc", borderRadius: "4px" }}>
                  <SignatureCanvas
                    ref={guardianSignatureRef}
                    canvasProps={{
                      width: 300,
                      height: 150,
                      className: "signature-canvas"
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={clearGuardianSignature}
                >
                  Clear Signature
                </button>
                <div className="mt-2">
                  <small>Date: {new Date().toLocaleDateString()}</small>
                </div>
              </div>
              
              <div className="col-md-6 mb-3">
                <label className="form-label">Lavni Clinical Director Signature:</label>
                <div style={{ border: "1px solid #ccc", borderRadius: "4px" }}>
                  <SignatureCanvas
                    ref={clinicalDirectorSignatureRef}
                    canvasProps={{
                      width: 300,
                      height: 150,
                      className: "signature-canvas"
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={clearClinicalDirectorSignature}
                >
                  Clear Signature
                </button>
                <div className="mt-2">
                  <small>Date: {new Date().toLocaleDateString()}</small>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ minWidth: "150px" }}
            >
              {isSubmitting ? "Submitting..." : "Submit Consent Form"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MinorConsentModalNC;
